import { api } from '../../core/network/api';
import type { ItemCopy } from '../config/item-copy';
import type { GearSlot } from '../config/gear-defs';

/**
 * design/gdd/equipment.md's worn-gear amendment — the paperdoll's four
 * previously locked slots.
 *
 * Served rather than mirrored, for the reason `equipment-api.ts` gives about
 * arms: every stat here is read off the catalogue at request time, so a
 * rebalance reaches the picker with no client edit.
 */

export interface GearStats {
  physicalReduction?: number;
  magicResistance?: number;
  poise?: number;
  moveSpeedDelta?: number;
  hpBonus?: number;
  staminaBonus?: number;
  carryKg?: number;
  /** Passive dodge chance, flat (2026-10-16): 0.01 is one percentage point. Weave feet/hands/trinkets. */
  dodgeChance?: number;
}

export interface GearCatalogRow {
  id: string;
  slot: GearSlot;
  tier: 1 | 2 | 3;
  /** WARD trades weight for survival; WEAVE trades armour for carry. */
  line: 'WARD' | 'WEAVE';
  name: string;
  weightKg: number;
  stats: GearStats;
  /** Present only on tier 1 — the ladder's upper rungs are found, not sold. */
  priceWax?: number;
  copy: ItemCopy;
  owned: boolean;
  /** The floor it drops on, named. */
  tierName: string;
  /**
   * What the vendor pays to take it back. Present on every piece, unlike
   * `priceWax` — a Vault Cuirass is unbuyable at any price and still worth
   * selling.
   */
  buybackWax: number;
  /** Which wallet the rung is quoted in — Cellars are Wax, Ossuary and Vaults Ember. */
  currency: 'SOFT' | 'PREMIUM';
  /**
   * Set on a Chandlery re-cut: the id of the Vaults piece whose numbers it
   * carries (2026-09-07). Present so the shelf can group them under their own
   * heading — they are bought and never found, and listing them under the
   * floor they copy would say they drop there.
   */
  variantOf?: string;
}

export interface GearBonuses {
  physicalReduction: number;
  magicResistance: number;
  poise: number;
  moveSpeedDelta: number;
  hpBonus: number;
  staminaBonus: number;
  carryKg: number;
  weightKg: number;
  /** Passive dodge chance, flat (2026-10-16). */
  dodgeChance: number;
}

export interface WornGear {
  worn: Record<GearSlot, GearCatalogRow | null>;
  bonuses: GearBonuses;
  defence: {
    armorClass: string;
    physicalReduction: number;
    magicResistance: number;
    poise: number;
    moveSpeedMultiplier: number;
    /** Class base + gear, clamped (2026-10-16). Optional: an older server does not send it. */
    dodgeChance?: number;
  };
}

export const getGearCatalog = (): Promise<GearCatalogRow[]> => api.get('/equipment/gear/catalog');

export const getGearShelf = (): Promise<GearCatalogRow[]> => api.get('/equipment/gear/shelf');

export const getWornGear = (characterId: string): Promise<WornGear> =>
  api.get(`/equipment/gear/${encodeURIComponent(characterId)}`);

/** `defId: null` empties the slot — every gear slot may legitimately be empty. */
export const equipGear = (characterId: string, slot: GearSlot, defId: string | null): Promise<WornGear> =>
  api.post(`/equipment/gear/${encodeURIComponent(characterId)}`, { slot, defId });

export const purchaseGear = (defId: string): Promise<{ softBalance: number; ownedIds: string[] }> =>
  api.post('/equipment/gear/purchase', { defId });

/**
 * Sell a worn piece back for Wax, minus the vendor's commission.
 *
 * **Irreversible for tiers 2 and 3.** No shelf sells them at any price, so
 * the only way to see the piece again is to go and find another — which is
 * why the caller confirms before firing, unlike the shop's Buy buttons.
 */
export const sellGear = (defId: string): Promise<import('./equipment-api').BuybackResult> =>
  api.post('/equipment/gear/sell', { defId });
